/**
 * Separate type file for types that may be shared with the app.
 * Prevents pulling in unnecessary types into CI.
 */
import type { Transformation } from "../../types.ts";
import type { ThresholdDefinition } from "../probabilistic/thresholds";

// Convenience aliases to make it more obvious when passing around a string that
// represents something specific.
export type UUID = string;
export type ColumnName = string;

export type IDRv2IdentifierMergeRule = {
  identifier: string;
  transformations?: Transformation[];
};

export type IDRv2MergeRules = {
  identifiers: IDRv2IdentifierMergeRule[];
};

export type IDRv2LimitRules = {
  [identifier: string]: {
    limit: number;
  };
};

export type IDRv2Run = {
  id: UUID;
  startedAt: Date;
  previousRunStats?: IDRv2RunStats;
  isFullRerun?: boolean;
  runHealthChecks?: boolean;
};

export type IDRv2TrainingRun = {
  id: UUID;
  dryRun?: boolean;
};

// Health computed at promotion time (health.ts).
export type IDRv2PromotionHealth = {
  // null when supercluster detection is disabled (no thresholds configured)
  supercluster_count?: number | null;
  // Number of clusters with unresolved limit rule violations
  limit_violation_count?: number;
};

// Stored shape of idr_runs.health.
export type IDRv2RunHealth = IDRv2PromotionHealth & {
  // Blocklist entries this run flagged for review.
  detected_blocklist_count?: number;
};

export type IDRv2ClassificationStats = {
  precision: number | null;
  recall: number | null;
  false_positive_rate: number | null;
};

export interface IDRv2StatsDeterministic {
  models: ModelStats[];
  identifiers: IdentifierStats[];
  num_ht_ids: number;
  source_rows: number;
  new_num_ht_ids: number;
  classification?: IDRv2ClassificationStats;
}

export type IDRv2StatsProbabilistic = IDRv2StatsDeterministic & {
  num_edges: number;
  new_num_edges: number;
  /** Intra-block pairs scored this run (new×new). Absent on runs before this field existed. */
  num_intra_pairs?: number;
  /** Cross-block pairs scored this run (new×working). Absent on runs before this field existed. */
  num_inter_pairs?: number;
  /** Intra + inter. Pre min_match_score; same pair can count twice across rules. */
  num_pair_comparisons?: number;
  /** Mapped pIDR identifiers. */
  num_identifiers?: number;
  /**
   * Blocking passed to pIDR: all eligible rules (true), cartesian (false),
   * or specific BlockingRule ids.
   */
  blocking?: boolean | string[];
  /** Rows exported to pIDR this run (full or incremental). Absent when not measured. */
  num_new_rows?: number;
  classification?: IDRv2ClassificationStats;
  // Classification including deterministic matches
  combined_classification?: IDRv2ClassificationStats;
};

export interface IDRv2Stats {
  deterministic: IDRv2StatsDeterministic;
  probabilistic: {
    threshold: ThresholdDefinition;
    stats: IDRv2StatsProbabilistic;
  }[];
}

export type IDRv2RunStats =
  | IDRv2StatsDeterministic // for legacy stats compatibility
  | IDRv2Stats;

export interface ModelStats {
  id: string;
  name: string;
  source_rows: number;
  num_ht_ids: number;
}

export interface IdentifierStats {
  name: string;
  source_rows: number;
  num_ht_ids: number;
}

export const IDRv2ResolvedTableName = "resolved";
export const IDRv2ResolvedTableSuffix = `_${IDRv2ResolvedTableName}`;

export const IDRv2OutputTableSuffix = "_resolved_identifiers";

export const IDRv2GoldenRecordTableSuffix = "_golden_records";
/** Warehouse table name segment (no leading underscore). */
export const IDRv2ScopedGoldenRecordTableName = "golden_records_scoped";
/** Display suffix appended to the user-configured output table prefix. */
export const IDRv2ScopedGoldenRecordTableSuffix = `_${IDRv2ScopedGoldenRecordTableName}`;
/**
 * One row per (identifier, value, ht_id, scope), so downstream SQL can look up
 * which scopes carried a value rather than which scopes a cluster spans.
 */
export const IDRv2ScopedResolvedIdentifiersTableName =
  "resolved_identifiers_scoped";
export const IDRV2GoldenRecordHightouchIdColumnName = "ht_id";
export const IDRV2GoldenRecordRowCountColumnName = "ht_row_count";
export const IDRV2GoldenRecordReservedColumnNames = [
  IDRV2GoldenRecordHightouchIdColumnName,
  "row_pk",
  "timestamp",
  "original_ht_id",
];

export enum StablePromotionErrorMessage {
  SuperclustersDetected = "superclusters detected",
  LimitViolationsDetected = "unresolved limit rule violations detected",
}

export const DEFAULT_WATERMARK_LOOKBACK_DAYS = 1;
